import React from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

interface TablePaginationProps {
  currentPage: number;
  totalCount: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const navButton =
    "w-9 h-9 flex items-center justify-center rounded-full bg-brand-500 text-white hover:bg-brand-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors";

  return (
    <div className="flex flex-wrap justify-between items-center gap-4 p-4 bg-white border-t border-gray-100 font-montserrat">
      {/* Pagination Controls */}
      <div className="flex items-center gap-3">
        <button
          className={navButton}
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <FaChevronLeft />
        </button>

        <span className="text-sm text-brand-gray font-medium">
          {t('pagination.pageOf', { current: currentPage, total: totalPages })}
        </span>

        <button
          className={navButton}
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Rows per page dropdown */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-brand-gray">{t('pagination.rowsPerPage')}</span>
        <select
          className="p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
          value={rowsPerPage}
          onChange={onRowsPerPageChange}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
        </select>
      </div>
    </div>
  );
};

export default TablePagination;
